import { cn } from '@/lib/utils'
import { Modal } from '@/components/Modal'
import type { OrderLine } from '../types'
import { MONO, fmt, fmtUnits } from './format'
import { UNCLASSIFIED, type LineRef, type SplitFig, type VolumeFig } from './classification'

/** Category drill-down — one Planning Center discipline, Lab category, Planning
 *  Center stage or the Unclassified remainder, for the active window: Gross vs
 *  PC credit vs True Sale, its share of the department and of the total, its
 *  units, and (once the order-level export supplies them) the itemised orders. */
export function CategoryDrill({
  onClose,
  line,
  departmentName,
  periodLabel,
  currency,
  fig,
  departmentFig,
  totalFig,
  volume,
  orders,
  ordersHint,
}: {
  onClose: () => void
  line: LineRef | null
  departmentName: string
  periodLabel: string
  currency: string
  fig: SplitFig
  departmentFig: SplitFig
  totalFig: SplitFig
  volume: VolumeFig | null
  /** Itemised orders for this category and month, when the payload carries them. */
  orders: OrderLine[] | undefined
  /** Why no orders can show (consolidated / multi-month range), or '' when none exist. */
  ordersHint: string
}) {
  const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0)
  const isStage = line?.kind === 'stage'
  const isUnclassified = line?.id === UNCLASSIFIED
  const units = volume ? volume.paidUnits + volume.includedUnits : 0

  return (
    <Modal
      open={!!line}
      onClose={onClose}
      eyebrow={line ? `${isUnclassified ? departmentName : isStage ? `${departmentName} › Stage` : departmentName} · ${periodLabel}` : ''}
      title={line?.name ?? ''}
      subtitle="Gross worked vs PC credit vs True Sale"
      footer={
        isStage
          ? 'A Planning Center stage is a second cut of the same Planning Center total (by stage instead of discipline). Itemised orders are listed per category.'
          : `Itemised orders appear here once the order-level export is supplied; the figures above are the split for this category and period.${volume ? ' Units are spread across months from the export’s period total.' : ''}`
      }
    >
      {line && (
        <>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-stone-500 dark:text-stone-400">Gross Sale</span>
              <span className={MONO}>{fmt(fig.gross, currency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-500 dark:text-stone-400">− PC credit consumed</span>
              <span className={cn('text-red-600 dark:text-red-400', MONO)}>{fig.pcCredit ? '−' + fmt(fig.pcCredit, currency) : '—'}</span>
            </div>
            <div className="flex items-center justify-between border-t border-stone-100 pt-2 dark:border-stone-800">
              <span className="font-medium">True Sale</span>
              <span className={cn('font-semibold', MONO)}>{fmt(fig.trueSale, currency)}</span>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-stone-400 dark:text-stone-500">
            {isUnclassified ? null : (
              <>
                {pct(fig.gross, departmentFig.gross)}% of the period&rsquo;s {departmentName} gross ·{' '}
              </>
            )}
            {pct(fig.gross, totalFig.gross)}% of Planning Center + Lab gross · {pct(fig.pcCredit, fig.gross)}% funded by PC credit.
          </p>

          {volume && (
            <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3 text-xs dark:border-stone-800 dark:bg-stone-800/30">
              <div>
                <span className="block text-stone-400 dark:text-stone-500">Paid units</span>
                <span className={cn('text-sm font-semibold', MONO)}>{fmtUnits(volume.paidUnits)}</span>
              </div>
              <div>
                <span className="block text-stone-400 dark:text-stone-500">Included units</span>
                <span className={cn('text-sm font-semibold', MONO)}>{fmtUnits(volume.includedUnits)}</span>
              </div>
              <div>
                <span className="block text-stone-400 dark:text-stone-500">Included share</span>
                <span className={cn('text-sm font-semibold', MONO)}>{pct(volume.includedUnits, units)}%</span>
              </div>
            </div>
          )}

          {!isStage &&
            (orders && orders.length ? (
              <div className="mt-6 border-t border-stone-200/70 pt-6 dark:border-stone-800">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                      <th className="pb-2.5 pr-3 text-left font-medium">Order</th>
                      <th className="pb-2.5 px-3 text-right font-medium">Units</th>
                      <th className="pb-2.5 px-3 text-right font-medium">Gross</th>
                      <th className="pb-2.5 px-3 text-right font-medium">PC credit</th>
                      <th className="pb-2.5 pl-3 text-right font-semibold text-stone-600 dark:text-stone-300">True Sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, i) => (
                      <tr key={i} className="even:bg-stone-50/50 dark:even:bg-stone-800/20">
                        <td className="py-2.5 pr-3 text-stone-700 dark:text-stone-200">
                          {o.clinic || o.ref || o.description || `Order ${i + 1}`}
                          {o.description && (o.clinic || o.ref) ? <span className="block text-[11px] text-stone-400">{o.description}</span> : null}
                        </td>
                        <td className={cn('py-2.5 px-3 text-right text-stone-500 dark:text-stone-400', MONO)}>{o.units != null ? o.units : '—'}</td>
                        <td className={cn('py-2.5 px-3 text-right', MONO)}>{fmt(o.gross, currency)}</td>
                        <td className={cn('py-2.5 px-3 text-right text-red-600 dark:text-red-400', MONO)}>{o.pcCredit ? '−' + fmt(o.pcCredit, currency) : '—'}</td>
                        <td className={cn('py-2.5 pl-3 text-right font-semibold', MONO)}>{fmt(o.trueSale != null ? o.trueSale : o.gross - (o.pcCredit || 0), currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-5 rounded-lg bg-stone-50 px-3.5 py-3 text-xs leading-relaxed text-stone-500 dark:bg-stone-800/50 dark:text-stone-400">
                No itemised orders for {line.name} in {periodLabel} in the current export{ordersHint}. Add the order-level data and each order will list here.
              </p>
            ))}
        </>
      )}
    </Modal>
  )
}
